<template>
  <div>
    <h1 class="mb-5 text-center">{{ $t('partnerPortal.calculateOffer') }}</h1>
    <div class="tabs">
      <dx-tabs
        :tabs-data="tabOptions"
        :value="clientType"
        @input="switchTariffs"
      />
    </div>
    <dx-tariffs
      v-if="usedTariffs.length > 0"
      :tariffs="usedTariffs"
      theme="partner"
      class="tariffFontCorrection"
    />
    <div v-else class="d-lg-flex mt-5 p-3">
      <h1>These tariffs are coming soon!</h1>
    </div>
    <div
      v-if="clientType === 'special'"
      class="d-lg-flex mt-5 p-3 special-tariff"
    >
      <h3
        class="mr-3 h4 icon-left"
        v-html="$t('partnerPortal.tariff.special.headline')"
      />
      <div class="px-2 py-3 px-lg-5 py-lg-4 content-right">
        {{ $t('partnerPortal.tariff.special.text') }}
      </div>
    </div>
  </div>
</template>

<script>
import {
  Tariff as DxTariffs,
  DxTabs,
} from '@sumcumo/dextra-frontend-component-lib'

import tariffQuery from '~/domains/partnerPortal/__gql__/queries/tariffs.gql'

export default {
  name: 'Products',
  components: {
    DxTabs,
    DxTariffs,
  },
  props: {
    isIframe: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      clientType: 'private',
      tabOptions: [
        {
          value: 'private',
          text: this.$t('partnerPortal.privateClient'),
          id: 'private',
        },
        {
          value: 'company',
          text: this.$t('partnerPortal.company'),
          id: 'company',
        },
        {
          value: 'special',
          text: this.$t('partnerPortal.special'),
          id: 'special',
        },
      ],
      tariffs: [],
    }
  },
  async fetch() {
    const { data } = await this.$apolloProvider.clients.cms.query({
      query: tariffQuery,
      variables: {
        locales: [this.$i18n.locale],
      },
    })

    if (data) {
      this.tariffs = data.page.edges.map((item) => this.tariffMapper(item.node))
    }
  },
  computed: {
    privateTariffs() {
      return this.tariffs
        .filter((tariff) => this.isPrivateTariff(tariff))
        .sort((a, _b) => {
          if (a.slug === 'privateIntroTariffL') {
            return -1
          }
          if (a.slug === 'privateIntroTariffFlex') {
            return 1
          }
          return 0
        })
    },
    businessTariffs() {
      return this.tariffs.filter((tariff) => this.isBusinessTariff(tariff))
    },
    specialTariffs() {
      return this.tariffs.filter((tariff) => this.isSpecialTariff(tariff))
    },
    usedTariffs() {
      if (this.clientType === 'special') {
        return this.specialTariffs
      }
      if (this.clientType === 'company') {
        return this.businessTariffs
      }
      return this.privateTariffs
    },
  },
  methods: {
    isBusinessTariff(tariff) {
      return tariff.slug.startsWith('business')
    },
    isPrivateTariff(tariff) {
      return tariff.slug.startsWith('private')
    },
    isSpecialTariff(tariff) {
      return !this.isBusinessTariff(tariff) && !this.isPrivateTariff(tariff)
    },
    getTariffBySlug(slug) {
      return this.tariffs.find((tariff) => tariff.slug === slug)
    },
    tariffMapper(tariff) {
      return {
        ...tariff,
        benefitHeadline: tariff.tariffBenefitList?.headline,
        benefits: tariff.tariffBenefitList?.listEntries,
        benefitCopy: tariff.tariffBenefitCopy,
        additionalItemsHeadline: tariff.tariffAdditionalList?.headline,
        additionalItems: tariff.tariffAdditionalList?.listEntries,
        tariffExtras: tariff.tariffExtrasList?.listEntries,
        tariffButtonRouteName: this.getOfferRoute(tariff),
        tariffMoreInfoUrl: this.getMoreRoute(tariff, 'broker'),
      }
    },
    getOfferRoute(tariff) {
      if (this.isSpecialTariff(tariff)) {
        return 'mailto:broker@dextra.ch'
      }

      const basePath = {
        default: {
          de: '/partnerportal/antragsstrecke/',
          fr: '/portailpartenaires/formulaire-de-demande/',
        },
        iframe: {
          de: '/antragsstrecke-iframe/',
          fr: '/formulaire-de-demande-iframe/',
        },
      }
      const isBusiness = this.isBusinessTariff(tariff)
      const productKey = isBusiness ? 'business' : 'private'
      let productVariant = isBusiness ? 'flex_business' : 'flex_dex'
      if (tariff.slug.includes('TariffXL')) {
        productVariant = isBusiness ? 'business_xl' : 'dex_xl'
      }
      if (tariff.slug.includes('TariffL')) {
        productVariant = isBusiness ? 'business_l' : 'dex_l'
      }

      const path =
        basePath[this.isIframe ? 'iframe' : 'default'][this.$i18n.locale]

      const queryParams = [`key=${productKey}`, `variantKey=${productVariant}`]

      const iframeParams = [
        `contractid=${this.$route.query.contractid}`,
        `jwt=${this.$route.query.jwt}`,
      ]

      const params = this.isIframe
        ? [...queryParams, ...iframeParams]
        : queryParams

      return this.localePath(`${path}?${params.join('&')}`)
    },
    getMoreRoute(tariff, type) {
      if (type !== 'broker') {
        return tariff.tariffMoreInfoUrl
      }
      const segmentSubpages = {
        business: {
          de: '/broker-firmenkunden/',
          fr: '/fr/courtiers-clients-entreprises/',
        },
        private: {
          de: '/broker-privatkunden/',
          fr: '/fr/courtiers-clients-prives/',
        },
      }
      const baseUrl = this.$nuxt.context.env.DEXTRA_WEBSITE_URL
      const segment = this.isBusinessTariff(tariff) ? 'business' : 'private'
      const segmentSubpage = segmentSubpages[segment][this.$i18n.locale]

      let variant = 'flex'
      if (tariff.slug.includes('TariffXL')) {
        variant = 'xl'
      }
      if (tariff.slug.includes('TariffL')) {
        variant = 'l'
      }
      return `${baseUrl}${segmentSubpage}?variant=${variant}`
    },

    switchTariffs(value) {
      if (this.clientType === value) {
        return
      }
      this.clientType = value
    },
  },
}
</script>

<style lang="scss" scoped>
@import './index';
</style>
